"use client"

import * as React from "react"
import { Slider as SliderPrimitive } from "radix-ui"

import { cn } from "../lib"

interface SliderProps extends React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> {
  /** Classes for the track (the full-width rail). */
  trackClassName?: string
  /** Classes for the filled portion between the minimum and the thumb. */
  rangeClassName?: string
  /** Classes for the thumb(s). */
  thumbClassName?: string
  /** Accessible name for the thumb — Radix puts the slider role on the thumb, not the root. */
  thumbAriaLabel?: string
}

const Slider = React.forwardRef<React.ElementRef<typeof SliderPrimitive.Root>, SliderProps>(
  (
    { className, value, defaultValue, trackClassName, rangeClassName, thumbClassName, thumbAriaLabel, ...props },
    ref,
  ) => {
    const thumbCount = Array.isArray(value) ? value.length : Array.isArray(defaultValue) ? defaultValue.length : 1

    return (
      <SliderPrimitive.Root
        ref={ref}
        className={cn("relative flex w-full touch-none select-none items-center", className)}
        value={value}
        defaultValue={defaultValue}
        {...props}
      >
        <SliderPrimitive.Track
          className={cn("relative h-2 w-full grow overflow-hidden rounded-full bg-secondary", trackClassName)}
        >
          <SliderPrimitive.Range className={cn("absolute h-full bg-primary", rangeClassName)} />
        </SliderPrimitive.Track>
        {Array.from({ length: Math.max(1, thumbCount) }).map((_, index) => (
          <SliderPrimitive.Thumb
            key={index}
            aria-label={thumbAriaLabel}
            className={cn(
              "keyboard-focus block h-5 w-5 rounded-full border-2 border-primary bg-background ring-offset-background transition-colors disabled:pointer-events-none disabled:opacity-50",
              thumbClassName,
            )}
          />
        ))}
      </SliderPrimitive.Root>
    )
  },
)
Slider.displayName = SliderPrimitive.Root.displayName

export { Slider }
